import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import {
  channelSlotKey,
  type ChannelKey,
  type ChannelMapping,
  type ChannelMappingErrorCode,
} from '../models/channel-mapping.model';
import type { WithSlice } from '@reduxjs/toolkit';

export interface ChannelMappingError {
  code: ChannelMappingErrorCode;
  detail: string;
}

export type ChannelStatus = 'idle' | 'loading' | 'saving' | 'success' | 'error';

export interface ChannelSlot {
  status: ChannelStatus;
  data: ChannelMapping | null;
  error: ChannelMappingError | null;
}

export interface ChannelMappingState {
  /** Slots keyed by `channelSlotKey(channel, phoneNumberId|null)`. */
  slots: Record<string, ChannelSlot>;
  /** Snapshot of `GET /channel-mapping` for surfaces that need the full list. */
  list: {
    status: ChannelStatus;
    data: ChannelMapping[];
    error: ChannelMappingError | null;
  };
}

const emptySlot = (): ChannelSlot => ({
  status: 'idle',
  data: null,
  error: null,
});

const initialState: ChannelMappingState = {
  slots: {},
  list: { status: 'idle', data: [], error: null },
};

const ensureSlot = (state: ChannelMappingState, key: string): ChannelSlot => {
  if (!state.slots[key]) state.slots[key] = emptySlot();
  return state.slots[key];
};

export const channelMappingSlice = createSlice({
  name: 'channelMapping',
  initialState,
  reducers: {
    setSlotLoading: (state, action: PayloadAction<{ key: string }>) => {
      const slot = ensureSlot(state, action.payload.key);
      slot.status = 'loading';
      slot.error = null;
    },
    setSlotSaving: (state, action: PayloadAction<{ key: string }>) => {
      const slot = ensureSlot(state, action.payload.key);
      slot.status = 'saving';
      slot.error = null;
    },
    setSlotData: (
      state,
      action: PayloadAction<{ key: string; data: ChannelMapping | null }>,
    ) => {
      const slot = ensureSlot(state, action.payload.key);
      slot.status = 'success';
      slot.data = action.payload.data;
      slot.error = null;
    },
    setSlotError: (
      state,
      action: PayloadAction<{ key: string; error: ChannelMappingError }>,
    ) => {
      const slot = ensureSlot(state, action.payload.key);
      slot.status = 'error';
      slot.error = action.payload.error;
    },
    clearSlot: (state, action: PayloadAction<{ key: string }>) => {
      const slot = ensureSlot(state, action.payload.key);
      slot.status = 'success';
      slot.data = null;
      slot.error = null;
    },

    setListLoading: (state) => {
      state.list.status = 'loading';
      state.list.error = null;
    },
    setListData: (state, action: PayloadAction<ChannelMapping[]>) => {
      state.list.status = 'success';
      state.list.data = action.payload;
      state.list.error = null;
    },
    setListError: (state, action: PayloadAction<ChannelMappingError>) => {
      state.list.status = 'error';
      state.list.error = action.payload;
    },
  },
});

export const {
  setSlotLoading,
  setSlotSaving,
  setSlotData,
  setSlotError,
  clearSlot,
  setListLoading,
  setListData,
  setListError,
} = channelMappingSlice.actions;

// Defensive against stale persisted snapshots: redux-persist may rehydrate
// an older slice shape (e.g., the previous `byChannel` keying) until the
// next dispatch writes the new shape. The `?? …` fallbacks keep selectors
// safe in that intermediate state.
const selectSlice = (state: RootState) =>
  (state.channelMapping as ChannelMappingState | undefined) ?? initialState;
export const selectChannelMappingState = selectSlice;
const selectSlots = (state: RootState): Record<string, ChannelSlot> =>
  selectSlice(state).slots ?? {};
const EMPTY_SLOT: ChannelSlot = emptySlot();
const EMPTY_LIST: ChannelMapping[] = [];

export const selectSlot =
  (channel: ChannelKey, phoneNumberId: string | null = null) =>
  (state: RootState): ChannelSlot =>
    selectSlots(state)[channelSlotKey(channel, phoneNumberId)] ?? EMPTY_SLOT;

export const selectSlotData =
  (channel: ChannelKey, phoneNumberId: string | null = null) =>
  (state: RootState) =>
    selectSlot(channel, phoneNumberId)(state).data;

export const selectSlotStatus =
  (channel: ChannelKey, phoneNumberId: string | null = null) =>
  (state: RootState) =>
    selectSlot(channel, phoneNumberId)(state).status;

export const selectSlotError =
  (channel: ChannelKey, phoneNumberId: string | null = null) =>
  (state: RootState) =>
    selectSlot(channel, phoneNumberId)(state).error;

export const selectMappingList = (state: RootState) =>
  selectSlice(state).list?.data ?? EMPTY_LIST;

export const selectMappingListStatus = (state: RootState) =>
  selectSlice(state).list?.status ?? 'idle';

export const makeSelectMappingsForChannel = (channel: ChannelKey) =>
  createSelector([selectMappingList], (list) =>
    list.filter((m) => m.channel === channel),
  );

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof channelMappingSlice> {}
}
